'use client';

import { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import ServicesScene from './ServicesScene';

const ServicesHero = () => {
    const containerRef = useRef<HTMLElement>(null);
    const badgeRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const descRef = useRef<HTMLParagraphElement>(null);

    useGSAP(() => {
        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

        gsap.set([badgeRef.current, titleRef.current, descRef.current], {
            y: 50,
            opacity: 0
        });

        tl.to(badgeRef.current, {
            y: 0,
            opacity: 1,
            duration: 0.8,
            delay: 0.2
        })
            .to(titleRef.current, {
                y: 0,
                opacity: 1,
                duration: 1,
            }, '-=0.4')
            .to(descRef.current, {
                y: 0,
                opacity: 1,
                duration: 0.8
            }, '-=0.6');

    }, { scope: containerRef });

    return (
        <section ref={containerRef} className="relative py-0 overflow-hidden min-h-[100dvh] lg:min-h-screen flex items-center justify-center bg-slate-50">
            {/* 3D Background - Full Width */}
            <div className="absolute inset-0 overflow-hidden">
                <ServicesScene />
            </div>

            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-50/30 to-slate-50/80 z-0 pointer-events-none" />

            {/* Mobile Logo (Top Left - Aligned Grid, Visible only on Mobile) */}
            <div className="absolute top-8 left-6 z-20 block lg:hidden">
                <div className="relative h-20 w-64">
                    <AppImage
                        src="/Logo_Home_Herosection.png"
                        alt="Site Map Genius"
                        fill
                        className="object-contain object-left !bg-transparent !animate-none"
                        style={{ objectFit: 'contain' }}
                        priority
                    />
                </div>
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10 w-full pt-20 lg:pt-0">
                {/* Content Wrapper for Mobile Readability (Layout only, no visual card) */}
                <div className="rounded-3xl p-6 lg:p-0">

                    {/* Badge */}
                    <div ref={badgeRef} className="inline-flex items-center space-x-2 px-4 py-2 bg-white/80 backdrop-blur-md rounded-full border border-indigo-100 shadow-lg mb-8">
                        <span className="flex h-2 w-2 relative">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                        </span>
                        <span className="text-sm font-bold text-indigo-900 uppercase tracking-widest">Our Capabilities</span>
                    </div>

                    {/* Headline */}
                    <h1 ref={titleRef} className="text-4xl sm:text-7xl lg:text-8xl font-black text-slate-900 mb-6 lg:mb-8 tracking-tight leading-tight">
                        Engineering <br className="hidden lg:block" />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-violet-600 to-blue-600 block lg:inline mt-2 lg:mt-0">
                            Digital Excellence
                        </span>
                    </h1>

                    {/* Subheadline */}
                    <p ref={descRef} className="max-w-2xl mx-auto text-base sm:text-xl text-slate-700 lg:text-slate-600 font-medium leading-relaxed drop-shadow-sm lg:drop-shadow-none">
                        We architect scalable, high-performance digital ecosystems. From pixel-perfect frontends to robust backend infrastructure, we build the technology that powers your growth.
                    </p>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce opacity-50 hidden lg:block">
                    <Icon name="ChevronDownIcon" className="w-8 h-8 text-slate-400" />
                </div>
            </div>
        </section>
    );
};

export default ServicesHero;
